import { useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

import Box from '~/common/components/Box';
import Button from '~/common/components/Button';
import {
  Table,
  Tbody,
  Td,
  Tfoot,
  Th,
  Thead,
  Tr,
} from '~/common/components/Tables';
import { formatDateWithTime } from '~/common/util/format';

import {
  IncomeReconciliationHeaderContent,
  RenderIncomeReconciliationRows,
} from './IncomeReconciliationUtils';
import { RowDetails } from './RenderingDetails';
import type {
  transformBoltAuditEndpointData,
  transformIncomeReconBoltAuditEndpointData,
} from './utils';

const StyledTbody = styled(Tbody)`
  & tr td:first-of-type {
    border-left: 1px solid ${(props) => props.theme.colors?.gray[200]};
  }
  & tr td:last-of-type {
    border-right: 1px solid ${(props) => props.theme.colors?.gray[200]};
  }
`;

const StyledTfoot = styled(Tfoot)`
  & tr td {
    border-bottom: 1px solid;
    border-top: 1px solid;
    border-color: ${(props) => props.theme.colors?.gray[200]};
  }
  & tr td:first-of-type {
    border-left: 1px solid ${(props) => props.theme.colors?.gray[200]};
  }
  & tr td:last-of-type {
    border-right: 1px solid ${(props) => props.theme.colors?.gray[200]};
  }
  padding: 8px;
`;

const HeaderTr = styled(Tr)`
  background-color: ${(props) => props.theme.colors?.teal[100]};
  & th {
    color: ${(props) => props.theme.colors?.white};
    border-bottom: 1px solid;
    border-color: ${(props) => props.theme.colors?.gray[200]};
  }
  & th:first-of-type {
    border-top-left-radius: 8px;
  }
  & th:last-of-type {
    border-top-right-radius: 8px;
  }
`;

const Badge = styled('span')`
  color: ${(props) => props.theme.colors?.white};
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  border-radius: 50%;
  line-height: 150%;
  background-color: ${(props) => props.theme.colors?.teal[100]};
`;

const IconButton = styled(FontAwesomeIcon)<{ disabled: boolean }>`
  cursor: pointer;
  color: ${(props) => props.theme.colors?.gray[600]};

  &:hover {
    color: ${(props) =>
      props.disabled
        ? props.theme.colors?.gray[300]
        : props.theme.colors?.teal[100]};
  }

  ${(props) => {
    if (props.disabled) {
      return `
        color: ${props.theme.colors?.gray[300]};
        cursor: not-allowed;
      `;
    }
  }}
`;

function DefaultHeaderContent(
  headers?: Readonly<{
    loanAction?: string;
    response?: string;
  }>,
) {
  return (
    <>
      <Th colSpan={1}>{headers?.loanAction ?? 'Loan Action'}</Th>
      <Th colSpan={1}>{headers?.response ?? 'Response'}</Th>
      <Th colSpan={1}>User</Th>
      <Th colSpan={1}>Date/Time</Th>
      <Th colSpan={1}>History</Th>
    </>
  );
}

export const RowTd = styled(Td)`
  word-break: break-word;
`;

type RowProps = {
  loanAction?: string;
  auditData?: AuditTableProps['data'][number]['value'];
};

type CombinedAuditTableProps = (
  | AuditTableProps
  | IncomeReconAuditTableProps
) & {
  sx?: React.ComponentProps<typeof Table>['sx'];
  maxPageSize?: number;
};

export type HistoryRowProps = {
  amount?: string;
  excluded?: string;
  fieldValue?: FieldValue;
  userName?: string;
  userEmail?: string;
  modifiedDateTime?: string;
};

type FieldValue =
  | undefined
  | string
  | string[]
  | {
      type: 'ledger';
      rows: ({ label: string; value: string } | null)[];
    };

type FieldValueDisplayProps = {
  fieldValue: FieldValue;
};

type AuditTableProps = {
  data?: ReturnType<
    typeof transformBoltAuditEndpointData
  >['entitiesAudit'][number]['fieldsAudit'];
  section: { type?: string; [key: string]: any };
  headers?: {
    loanAction?: string;
    response?: string;
  };
  isRentalIncomeSection?: boolean;
};

export type IncomeReconAuditTableProps = {
  data?: ReturnType<
    typeof transformIncomeReconBoltAuditEndpointData
  >['entity'][number]['auditSections'];
  section: { type?: string; [key: string]: any };
  headers?: {
    loanAction?: string;
    response?: string;
  };
  isRentalIncomeSection?: boolean;
};

export default function AuditTable({
  data = [],
  section,
  headers,
  isRentalIncomeSection = false,
  sx,
  maxPageSize,
}: Readonly<CombinedAuditTableProps>) {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = maxPageSize ?? 10;
  const totalEntries = data.length;
  const totalPages = Math.ceil(totalEntries / pageSize);
  const paginatedEntries = data.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <Table data-testid="audit-history--table" sx={sx}>
      <Thead>
        <HeaderTr>
          {isRentalIncomeSection
            ? IncomeReconciliationHeaderContent(section)
            : DefaultHeaderContent(headers)}
        </HeaderTr>
      </Thead>
      {isRentalIncomeSection ? (
        <StyledTbody>
          {RenderIncomeReconciliationRows(paginatedEntries)}
        </StyledTbody>
      ) : (
        renderDefaultRows(paginatedEntries)
      )}
      {totalPages > 1 && (
        <StyledTfoot>
          <Tr>
            <Td colSpan={isRentalIncomeSection ? 6 : 5}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-around',
                  alignItems: 'center',
                  maxWidth: '350px',
                  margin: '0 auto',
                }}
              >
                <Button
                  type="button"
                  aria-label="chevron-left"
                  data-testid="chevron-left"
                  disabled={currentPage === 1}
                  onClick={() =>
                    currentPage !== 1 && handlePageChange(currentPage - 1)
                  }
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <IconButton
                    icon={faChevronLeft as IconProp}
                    disabled={currentPage === 1}
                  />
                </Button>
                <Badge data-testid="pagination-badge">{currentPage}</Badge>
                <Button
                  type="button"
                  aria-label="chevron-right"
                  data-testid="chevron-right"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    currentPage !== totalPages &&
                    handlePageChange(currentPage + 1)
                  }
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor:
                      currentPage === totalPages ? 'not-allowed' : 'pointer',
                  }}
                >
                  <IconButton
                    icon={faChevronRight as IconProp}
                    disabled={currentPage === totalPages}
                  />
                </Button>
              </Box>
            </Td>
          </Tr>
        </StyledTfoot>
      )}
    </Table>
  );
}

function renderDefaultRows(entries) {
  return entries.map(({ key, value }) => (
    <Row key={key} loanAction={key} auditData={value} />
  ));
}

function Row({ auditData = [], loanAction = '' }: Readonly<RowProps>) {
  const theme = useTheme();
  const [showHistory, setShowHistory] = useState(false);
  const handleClick = () => {
    setShowHistory(!showHistory);
  };
  const [lastEntry, ...restEntries] = auditData;
  const { fieldValue, userEmail, modifiedDateTime, userName } = lastEntry || {};
  const isShowHistoryActive = !!restEntries.length;

  const loanActionText = loanAction.replaceAll(' ', '');
  return (
    <StyledTbody
      id={`group-${loanActionText}`}
      aria-labelledby={`group-${loanActionText}-action`}
    >
      <Tr
        data-testid={loanAction}
        sx={{
          borderLeft: '1px solid',
          _hover: { background: theme.colors?.bgGray[300] },
        }}
      >
        <RowTd id={`group-${loanActionText}-action`} colSpan={1}>
          <strong>{loanAction}</strong>
        </RowTd>
        <RowTd colSpan={1}>
          <FieldValueDisplay fieldValue={fieldValue} />
        </RowTd>

        {RowDetails(
          loanAction,
          userName,
          userEmail,
          modifiedDateTime,
          isShowHistoryActive,
          handleClick,
          showHistory,
        )}
      </Tr>
      {showHistory &&
        !!restEntries.length &&
        restEntries.map((entry) => {
          return (
            <HistoryRow key={loanAction + `${entry.fieldValue}`} {...entry} />
          );
        })}
    </StyledTbody>
  );
}

function HistoryRow({
  fieldValue,
  userName,
  userEmail,
  modifiedDateTime,
}: Readonly<HistoryRowProps>) {
  return (
    <Tr data-testid="HistoryRow">
      <Td>&nbsp;</Td>
      <Td>
        <FieldValueDisplay fieldValue={fieldValue} />
      </Td>
      <Td>
        <b>{userName ?? 'Username Not Available'}</b>
        <br />
        {userEmail}
      </Td>
      <Td>{formatDateWithTime(modifiedDateTime)}</Td>
      <Td>&nbsp;</Td>
    </Tr>
  );
}

export function FieldValueDisplay(props: Readonly<FieldValueDisplayProps>) {
  if (props.fieldValue === undefined) {
    return null;
  }

  if (typeof props.fieldValue === 'string') {
    return <span>{props.fieldValue}</span>;
  }

  if (Array.isArray(props.fieldValue)) {
    return (
      <>
        {props.fieldValue.map((item) => (
          <div key={item}>{item}</div>
        ))}
      </>
    );
  }

  return (
    <>
      {props.fieldValue.rows.map((item) => (
        <div
          key={item?.value}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            height: item === null ? '1rem' : 'auto',
          }}
        >
          {item !== null && (
            <>
              <span>
                <strong>{item.label}</strong>:
              </span>
              <span>{item.value}</span>
            </>
          )}
        </div>
      ))}
    </>
  );
}
